import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  getServices,
  deleteService,
  createService,
  updateService,
} from '../../../services/api'
import type { Service } from '../../../types/database'

const springConfig = { type: "spring", stiffness: 60, damping: 10 }

export default function DashboardServices() {
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editingService, setEditingService] = useState<Service | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: '',
    category: 'tutoring' as 'tutoring' | 'web-dev',
    features: [] as string[],
    order: 0,
  })
  const [featureInput, setFeatureInput] = useState('')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    const servicesData = await getServices()
    setServices(servicesData)
    setLoading(false)
  }

  const handleCreate = () => {
    setEditingService(null)
    setError(null)
    setFormData({
      title: '',
      description: '',
      icon: '',
      category: 'tutoring',
      features: [],
      order: services.length,
    })
    setFeatureInput('')
    setIsFormOpen(true)
  }

  const handleEdit = (service: Service) => {
    setEditingService(service)
    setError(null)
    setFormData({
      title: service.title || '',
      description: service.description || '',
      icon: service.icon || '',
      category: service.category || 'tutoring',
      features: service.features || [],
      order: service.order || 0,
    })
    setFeatureInput('')
    setIsFormOpen(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this service?')) return
    const success = await deleteService(id)
    if (success) {
      loadData()
    }
  }

  const handleSave = async () => {
    // Validate form
    if (!formData.title.trim()) {
      setError('Title is required')
      return
    }
    if (!formData.description.trim()) {
      setError('Description is required')
      return
    }
    if (!formData.icon.trim()) {
      setError('Icon is required')
      return
    }

    setSaving(true)
    setError(null)

    try {
      if (editingService) {
        await updateService(editingService.id, formData)
      } else {
        await createService(formData as Omit<Service, 'id' | 'created_at' | 'updated_at'>)
      }
      setIsFormOpen(false)
      setEditingService(null)
      setFormData({
        title: '',
        description: '',
        icon: '',
        category: 'tutoring',
        features: [],
        order: 0,
      })
      setFeatureInput('')
      loadData()
    } catch (error) {
      console.error('Error saving service:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to save service. Please try again.'
      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const addFeature = () => {
    if (featureInput.trim() && !formData.features.includes(featureInput.trim())) {
      setFormData({
        ...formData,
        features: [...formData.features, featureInput.trim()],
      })
      setFeatureInput('')
    }
  }

  const removeFeature = (feature: string) => {
    setFormData({
      ...formData,
      features: formData.features.filter((f) => f !== feature),
    })
  }

  if (loading) {
    return <div className="text-text-primary/70">Loading services...</div>
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-semibold text-text-primary">Services</h2>
        <motion.button
          onClick={handleCreate}
          className="px-5 sm:px-6 py-2.5 sm:py-3 neu rounded-xl font-medium text-text-primary neu-hover touch-manipulation w-full sm:w-auto text-sm sm:text-base"
          whileHover={{ scale: 1.02, y: -1 }}
          whileTap={{ scale: 0.98 }}
          transition={springConfig}
        >
          + New Service
        </motion.button>
      </div>

      {services.length === 0 ? (
        <div className="neu rounded-2xl p-8 sm:p-12 text-center">
          <p className="text-text-primary/70 mb-4 text-sm sm:text-base">No services configured yet.</p>
          <button
            onClick={handleCreate}
            className="text-accent hover:text-accent-secondary font-medium transition-colors touch-manipulation text-sm sm:text-base py-2"
          >
            Add your first service
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {services.map((service) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="neu rounded-2xl p-4 sm:p-6 neu-hover"
            >
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl sm:text-4xl">{service.icon}</span>
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-text-primary">
                      {service.title}
                    </h3>
                    <span className="text-xs text-accent uppercase tracking-wide">
                      {service.category === 'tutoring' ? 'Tutoring' : 'Web Development'}
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-text-primary/70 text-xs sm:text-sm mb-4 line-clamp-2">
                {service.description}
              </p>
              <div className="flex gap-2">
                <motion.button
                  onClick={() => handleEdit(service)}
                  className="flex-1 px-3 sm:px-4 py-2.5 sm:py-2 neu rounded-xl text-text-primary hover:text-accent transition-colors text-xs sm:text-sm font-medium neu-hover touch-manipulation"
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  transition={springConfig}
                >
                  Edit
                </motion.button>
                <motion.button
                  onClick={() => handleDelete(service.id)}
                  className="px-3 sm:px-4 py-2.5 sm:py-2 neu rounded-xl text-red-400 hover:text-red-500 transition-colors text-xs sm:text-sm font-medium neu-hover touch-manipulation"
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  transition={springConfig}
                >
                  Delete
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {isFormOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
            onClick={() => setIsFormOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="neu rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto custom-scrollbar p-4 sm:p-8 m-4 sm:m-0"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-semibold text-text-primary">
                  {editingService ? 'Edit Service' : 'New Service'}
                </h2>
                <button
                  onClick={() => {
                    setIsFormOpen(false)
                    setError(null)
                  }}
                  disabled={saving}
                  className="text-text-primary/70 hover:text-text-primary text-xl sm:text-2xl transition-colors touch-manipulation p-2 -mr-2 disabled:opacity-50"
                >
                  ×
                </button>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 p-3 sm:p-4 neu bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/50 rounded-xl text-red-600 dark:text-red-400 text-xs sm:text-sm"
                >
                  {error}
                </motion.div>
              )}

              <form onSubmit={(e) => {
                e.preventDefault()
                handleSave()
              }} className="space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                    placeholder="Web Development"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                    Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={3}
                    className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all resize-none font-normal text-base touch-manipulation"
                    placeholder="A brief description of the service"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                    Icon (Emoji) *
                  </label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    required
                    className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                    placeholder="💻"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as 'tutoring' | 'web-dev' })}
                    className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation appearance-none"
                    style={{ color: 'inherit' }}
                  >
                    <option value="tutoring">Tutoring</option>
                    <option value="web-dev">Web Development</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                    Features
                  </label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={featureInput}
                      onChange={(e) => setFeatureInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          addFeature()
                        }
                      }}
                      className="flex-1 px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                      placeholder="Add a feature (e.g., React Development)"
                    />
                    <motion.button
                      type="button"
                      onClick={addFeature}
                      className="px-4 py-2.5 sm:py-2 neu rounded-xl text-accent hover:text-accent-secondary transition-colors neu-hover touch-manipulation text-sm sm:text-base"
                      whileHover={{ scale: 1.02, y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      transition={springConfig}
                    >
                      Add
                    </motion.button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.features.map((feature, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-2 px-3 py-1 neu rounded-full text-sm text-accent"
                      >
                        {feature}
                        <button
                          type="button"
                          onClick={() => removeFeature(feature)}
                          className="hover:text-accent-secondary transition-colors"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                    Order
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <motion.button
                    type="submit"
                    disabled={saving}
                    className="flex-1 px-5 sm:px-6 py-3 neu rounded-xl font-medium text-text-primary neu-hover disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation text-sm sm:text-base"
                    whileHover={saving ? {} : { scale: 1.02, y: -1 }}
                    whileTap={saving ? {} : { scale: 0.98 }}
                    transition={springConfig}
                  >
                    {saving ? 'Saving...' : editingService ? 'Update Service' : 'Create Service'}
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => {
                      setIsFormOpen(false)
                      setError(null)
                    }}
                    disabled={saving}
                    className="px-5 sm:px-6 py-3 neu rounded-xl font-medium text-text-primary/70 hover:text-text-primary transition-colors neu-hover touch-manipulation text-sm sm:text-base disabled:opacity-50"
                    whileHover={saving ? {} : { scale: 1.02, y: -1 }}
                    whileTap={saving ? {} : { scale: 0.98 }}
                    transition={springConfig}
                  >
                    Cancel
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}


